import mlClient from "@/ml-client";
import {
  SubscriptionSchema,
  BettorBasic,
  SubscriptionPlanModel,
  SubscriptionPlansSchema,
  SubscriptionPricesSchema,
  CreateSubscriptionPlan,
  CreateSubscriptionPrice,
  UpdateSubscriptionPrice,
} from "common";

export interface SubscriptionPackage extends SubscriptionPlanModel {}

export interface SubscriptionPackageWithStatus extends SubscriptionPackage {
  isSubscribed: boolean;
}

export interface PurchasedSubscription extends SubscriptionSchema {
  plan?: SubscriptionPackage;
  seller?: BettorBasic;
}

export async function getSellerSubscriptionPlans(
  token?: string | null
): Promise<SubscriptionPackage[]> {
  const result = await mlClient.get<SubscriptionPackage[]>(
    "/marketplace/subscription-plans",
    token
  );
  console.log("get result", result);

  if (!result.ok) {
    console.error("Failed to fetch seller subscription plans:", result.message);
    return [];
  }

  return result.data || [];
}

export async function getPublicSellerSubscriptionPlans(
  sellerId: number
): Promise<SubscriptionPackage[]> {
  // This function is deprecated - we should use getCapperProfileByHandle instead
  // Keeping it for backward compatibility but it won't work
  console.warn(
    "getPublicSellerSubscriptionPlans is deprecated - use getCapperProfile instead"
  );
  return [];
}

export async function getSubscriptionPackageById(
  packageId: number,
  capperHandle?: string
): Promise<SubscriptionPackage | null> {
  try {
    console.log(
      "getSubscriptionPackageById called with packageId:",
      packageId,
      "capperHandle:",
      capperHandle
    );

    if (!capperHandle) {
      console.warn(
        "getSubscriptionPackageById called without capperHandle - cannot fetch package"
      );
      return null;
    }

    console.log("Fetching capper profile for handle:", capperHandle);
    const result = await mlClient.get<any>(`/cappers/${capperHandle}`);

    if (!result.ok) {
      console.error("Failed to fetch capper profile:", result.message);
      return null;
    }

    const capperProfile = result.data;
    console.log("Fetched capper profile:", capperProfile);

    if (!capperProfile.plans || !Array.isArray(capperProfile.plans)) {
      console.error("No plans found in capper profile");
      return null;
    }

    const foundPackage = capperProfile.plans.find(
      (pkg: any) => pkg.id === packageId
    );
    console.log("Found package:", foundPackage);

    if (foundPackage) {
      return foundPackage;
    } else {
      console.error(
        "Package not found in capper's plans. Available package IDs:",
        capperProfile.plans.map((p: any) => p.id)
      );
      return null;
    }
  } catch (error) {
    console.error("Error fetching subscription package by ID:", error);
    return null;
  }
}

export async function getPublicSellerSubscriptionPlansWithStatus(
  sellerId: number,
  authToken?: string
): Promise<SubscriptionPackageWithStatus[]> {
  const packages = await getPublicSellerSubscriptionPlans(sellerId);

  if (!authToken) {
    return packages.map((pkg) => ({
      ...pkg,
      isSubscribed: false,
    }));
  }

  try {
    const userSubscriptions = await getUserSubscriptionsWithAuth(authToken);

    if (!userSubscriptions || !Array.isArray(userSubscriptions)) {
      return packages.map((pkg) => ({
        ...pkg,
        isSubscribed: false,
      }));
    }

    return packages.map((pkg) => ({
      ...pkg,
      isSubscribed: userSubscriptions.some(
        (sub: any) => sub.plan?.id === pkg.id && sub.status === "active"
      ),
    }));
  } catch (error) {
    console.error("Error checking subscription status:", error);
    return packages.map((pkg) => ({
      ...pkg,
      isSubscribed: false,
    }));
  }
}

export async function convertCapperPlansToPackagesWithStatus(
  plans: SubscriptionPlanModel[],
  authToken?: string
): Promise<SubscriptionPackageWithStatus[]> {
  if (!plans || !Array.isArray(plans) || plans.length === 0) {
    console.log("No plans provided or plans array is empty");
    return [];
  }

  if (!authToken) {
    return plans.map((plan) => ({
      ...plan,
      isSubscribed: false,
    }));
  }

  try {
    const userSubscriptions = await getUserSubscriptionsWithAuth(authToken);

    if (!userSubscriptions || !Array.isArray(userSubscriptions)) {
      return plans.map((plan) => ({
        ...plan,
        isSubscribed: false,
      }));
    }

    return plans.map((plan) => ({
      ...plan,
      isSubscribed: userSubscriptions.some(
        (sub: any) => sub.plan?.id === plan.id && sub.status === "active"
      ),
    }));
  } catch (error) {
    console.error("Error checking subscription status:", error);
    return plans.map((plan) => ({
      ...plan,
      isSubscribed: false,
    }));
  }
}

export async function getUserSubscriptionsWithAuth(
  token: string
): Promise<any[] | null> {
  const result = await mlClient.get<{ data: any[] }>(
    "/marketplace/subscriptions",
    token
  );

  console.log("getUserSubscriptionsWithAuth result:", result);

  if (!result.ok) {
    console.error(
      "Failed to fetch user subscriptions with auth:",
      result.message
    );
    return null;
  }

  return result.data?.data || [];
}

export async function createSubscriptionPlan(
  plan: CreateSubscriptionPlan,
  token?: string | null
): Promise<{ id: number } | null> {
  const result = await mlClient.post<{ plan_id: number }>(
    "/marketplace/subscription-plans",
    plan,
    token
  );

  if (!result.ok) {
    console.error("Failed to create subscription plan:", result.message);
    return null;
  }

  // Return the plan_id from the new API response format
  if (result.data?.plan_id) {
    return { id: result.data.plan_id };
  }

  console.error("API returned success but no plan_id in response");
  return null;
}

export async function createSubscriptionPrice(
  price: CreateSubscriptionPrice,
  token?: string | null
): Promise<SubscriptionPricesSchema | null> {
  const result = await mlClient.post<SubscriptionPricesSchema>(
    "/marketplace/subscription-prices",
    price,
    token
  );

  if (!result.ok) {
    console.error("Failed to create subscription price:", result.message);
    return null;
  }

  return result.data || null;
}

export async function updateSubscriptionPlan(
  planId: number,
  updates: Partial<{
    name: string;
    description: string;
    status: "draft" | "active" | "archived";
    default_price_id: number;
  }>,
  token?: string | null
): Promise<SubscriptionPackage | null> {
  const result = await mlClient.put<SubscriptionPackage>(
    `/marketplace/subscription-plans`,
    { plan_id: planId, ...updates },
    token
  );

  if (!result.ok) {
    console.error("Failed to update subscription plan:", result.message);
    return null;
  }

  return result.data || null;
}

export async function updateSubscriptionPlanPrice(
  planId: number,
  priceData: {
    amount_cents: number;
    interval: "day" | "week" | "month" | "year";
    interval_count: number;
  },
  token?: string | null
): Promise<SubscriptionPackage | null> {
  const result = await mlClient.put<SubscriptionPackage>(
    `/marketplace/seller/plans/${planId}/price`,
    priceData,
    token
  );

  if (!result.ok) {
    console.error("Failed to update subscription plan price:", result.message);
    return null;
  }

  return result.data || null;
}

export const updateSubscriptionPrice = async (
  priceId: string,
  updates: UpdateSubscriptionPrice,
  token?: string | null
): Promise<boolean> => {
  try {
    console.log("[updateSubscriptionPrice] Updating price:", priceId, updates);

    const payload = {
      ...updates,
      price_id: priceId,
    };

    const response = await mlClient.put(
      `/marketplace/subscription-prices`,
      payload,
      token
    );

    if (response.ok) {
      console.log("[updateSubscriptionPrice] Price updated successfully");
      return true;
    } else {
      console.error(
        "[updateSubscriptionPrice] Failed to update price:",
        response.message
      );
      return false;
    }
  } catch (error) {
    console.error("[updateSubscriptionPrice] Error updating price:", error);
    return false;
  }
};

export async function getSubscriptionIntent(
  capperHandle: string,
  planId: number,
  priceId: number,
  token?: string | null
): Promise<any | null> {
  const result = await mlClient.post<any>(
    "/marketplace/subscriptions/intent",
    {
      capper_handle: capperHandle,
      plan_id: planId,
      price_id: priceId,
    },
    token
  );

  if (!result.ok) {
    console.error("Failed to create subscription intent:", result.message);
    return null;
  }

  return result.data || null;
}

export async function initiateSubscription(
  data: {
    capper_handle: string;
    plan_id: number;
    price_id: number;
    payment_method_id?: string;
    save_payment_method?: boolean;
  },
  token?: string | null
): Promise<any | null> {
  const result = await mlClient.post<any>(
    "/marketplace/subscriptions/initiate",
    {
      capper_handle: data.capper_handle,
      plan_id: data.plan_id,
      price_id: data.price_id,
      selected_payment_method_id: data.payment_method_id,
      save_payment_method: data.save_payment_method ?? true,
    },
    token
  );

  if (!result.ok) {
    console.error("Failed to initiate subscription:", result.message);
    return null;
  }

  return result.data || null;
}

export async function isUserSubscribedToPlan(
  planId: number,
  token?: string | null
): Promise<boolean> {
  try {
    if (!token) {
      return false;
    }

    const userSubscriptions = await getUserSubscriptionsWithAuth(token);

    if (!userSubscriptions || !Array.isArray(userSubscriptions)) {
      return false;
    }

    // Check if the user has an active subscription to this specific plan
    return userSubscriptions.some(
      (sub: any) =>
        sub.plan?.id === planId &&
        (sub.status === "active" || sub.status === "trialing")
    );
  } catch (error) {
    console.error("Error checking if user is subscribed to plan:", error);
    return false;
  }
}

export async function getUserSubscriptions(
  token?: string | null
): Promise<any | null> {
  const result = await mlClient.get<{ data: any[] }>(
    "/marketplace/subscriptions",
    token
  );
  console.log("getUserSubscriptions result:", result);

  if (!result.ok) {
    if (result.code === 404) {
      console.log("No subscriptions found (404) - returning empty array");
      return [];
    }
    console.error("Failed to fetch user subscriptions:", result.message);
    return null;
  }

  return result.data || [];
}

export async function cancelSubscription(
  subscriptionId: number,
  token?: string | null
): Promise<boolean> {
  const result = await mlClient.post(
    "/marketplace/subscriptions/cancel",
    {
      subscription_id: subscriptionId,
    },
    token
  );

  if (!result.ok) {
    console.error("Failed to cancel subscription:", result.message);
    return false;
  }

  return true;
}

export async function cancelSubscriptionAtPeriodEnd(
  stripeSubscriptionId: string,
  cancelAtPeriodEnd: boolean,
  token?: string | null
): Promise<boolean> {
  const result = await mlClient.post(
    "/marketplace/subscriptions/cancel/at-period-end",
    {
      stripe_subscription_id: stripeSubscriptionId,
      cancel_at_period_end: cancelAtPeriodEnd,
    },
    token
  );

  if (!result.ok) {
    console.error(
      "Failed to update subscription cancellation:",
      result.message
    );
    return false;
  }

  return true;
}

export async function cancelSubscriptionNow(
  stripeSubscriptionId: string,
  token?: string | null
): Promise<boolean> {
  const result = await mlClient.post(
    "/marketplace/subscriptions/cancel/now",
    {
      stripe_subscription_id: stripeSubscriptionId,
    },
    token
  );

  if (!result.ok) {
    console.error("Failed to cancel subscription immediately:", result.message);
    return false;
  }

  return true;
}

export async function resubscribeSubscription(
  subscriptionId: number,
  token?: string | null
): Promise<boolean> {
  const result = await mlClient.post(
    "/marketplace/subscriptions/resubscribe",
    {
      subscription_id: subscriptionId,
    },
    token
  );

  if (!result.ok) {
    console.error("Failed to resubscribe subscription:", result.message);
    return false;
  }

  return true;
}

export async function deleteSellerProfile(
  bettorId: number,
  token?: string | null
): Promise<boolean> {
  const result = await mlClient.delete(
    `/marketplace/seller/${bettorId}`,
    token
  );

  if (!result.ok) {
    console.error("Failed to delete seller profile:", result.message);
    return false;
  }

  return true;
}

export interface SubscriberInfo {
  id: number;
  date_subscribed: string;
  renewal_date: string;
  status: string;
  cancel_at_period_end: boolean;
  subscription_type: string;
  amount_cents: number;
  interval: "week" | "month" | "year";
  interval_count: number;
  subscriber_id: number;
  subscriber_username: string;
  subscriber_avatar?: string;
  subscriber_display_name?: string;
}

export async function getMySubscribers(
  token?: string | null,
  options?: {
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
    order_by?: "created_desc" | "created_asc";
  }
): Promise<{
  subscribers: SubscriberInfo[];
  pagination?: { totalCount: number; totalPages: number; currentPage: number };
}> {
  try {
    let url = "/cappers/subscribers";

    if (options) {
      const params = new URLSearchParams();
      if (options.dateFrom) params.append("since", options.dateFrom);
      if (options.dateTo) params.append("until", options.dateTo);
      if (options.page) params.append("page", options.page.toString());
      if (options.limit) params.append("limit", options.limit.toString());
      if (options.order_by) params.append("order_by", options.order_by);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }
    }

    const result = await mlClient.get<{ rows: any[]; pagination: any }>(
      url,
      token
    );

    if (!result.ok) {
      console.error("Failed to fetch subscribers:", result.message);
      return { subscribers: [] };
    }

    const subscribersData = result.data?.rows || [];
    const paginationData = result.data?.pagination;

    const transformedData = subscribersData.map((subscriber: any) => ({
      id: subscriber.subscription_id,
      date_subscribed: subscriber.created_at,
      subscription_type: subscriber.plan_name,
      renewal_date: subscriber.current_period_end,
      cancel_at_period_end: subscriber.cancel_at_period_end,
      status: subscriber.cancel_at_period_end ? "cancelled" : "active",
      amount_cents: 0,
      interval: "month" as const,
      interval_count: 1,
      subscriber_id: subscriber.bettor?.id || 0,
      subscriber_username: subscriber.bettor?.username || "Unknown",
      subscriber_avatar: subscriber.bettor?.avatar_url || null,
      subscriber_display_name: subscriber.bettor?.display_name || null,
    }));

    return {
      subscribers: transformedData,
      pagination: paginationData,
    };
  } catch (error) {
    console.error("Error fetching subscribers:", error);
    return { subscribers: [] };
  }
}
